import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Descriptions,
  Input,
  Modal,
  Spin,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import PageHeaderAction from '../../../components/PageHeaderAction';
import TrackingChain from '../../../components/TrackingChain';
import AttachmentUpload from '../../../components/AttachmentUpload';
import CanvassForm from '../CanvassForm';
import SupplierDrawer from './SupplierDrawer';
import QuoteDrawer from './QuoteDrawer';
import QuoteCell from './QuoteCell';
import QtyCell from './QtyCell';
import {
  useCanvassStore,
  useFetchData,
  useFetchFile,
  useUpdateData,
} from '../../../hooks';
import {
  CANVASS_API,
  useAward,
  useSubmitCanvass,
  useGeneratePurchaseOrders,
  useSaveItem,
  useSaveQuote,
} from '../../../hooks/useCanvassHooks';
import { useAuthStore, useDrawerStore } from '../../../store';
import { BaseModel, PageableResponse } from '../../../types';
import {
  CanvassItemRecord,
  CanvassLineRecord,
  CanvassRecord,
} from '../../../types/Canvass';
import { Resource } from '../../../lib/enums';
import {
  convertDate,
  dateFormat,
  isModuleApproved,
  isUserCanEdit,
} from '../../../lib/utils';

const statusColour: Record<string, string> = {
  DRAFT: 'default',
  CONFIRM: 'processing',
  APPROVED: 'success',
  CANCEL: 'error',
};

/**
 * The comparison: materials down, suppliers across.
 *
 * A request here averages 120 lines, so this is the whole point of the screen --
 * one canvass, one set of supplier conversations, and normally one order. The
 * award defaults to a single supplier and splits only where it has to.
 */
const CanvassInfoPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const drawerStore = useDrawerStore();
  const canvassStore = useCanvassStore();
  const canEdit = isUserCanEdit(user?.moduleAccess, 'Canvass');
  // Awarding is the decision at step 10, not an edit. Offering the button to
  // everyone who can edit only produced a 403 at the end of the click.
  const canApprove =
    user?.role === 'ROLE_ADMIN' ||
    isModuleApproved(user?.moduleAccess, 'Canvass');

  const [supplierDrawer, setSupplierDrawer] = useState(false);
  const [editingSupplier, setEditingSupplier] =
    useState<CanvassLineRecord | null>(null);
  const [quoting, setQuoting] = useState<{
    line: CanvassLineRecord;
    item: CanvassItemRecord;
  } | null>(null);
  const [awarding, setAwarding] = useState<CanvassLineRecord | null>(null);
  const [awardRemarks, setAwardRemarks] = useState('');

  const { data, isLoading } = useFetchData<CanvassRecord>(
    `${CANVASS_API}/${params.id}`,
  );
  const { data: files } = useFetchFile(
    `${CANVASS_API}/${params.id}/files`,
    'api/purchase/canvasses',
  );
  const { data: vendorData } = useFetchData<PageableResponse<BaseModel>>(
    '/partner/vendors',
    { resource: Resource.NAME, length: 999999 },
  );

  const { mutate: update } = useUpdateData(CANVASS_API);
  const { mutate: saveQuote } = useSaveQuote(params.id);
  const { mutate: saveItem } = useSaveItem(params.id);
  const { mutate: award } = useAward(params.id);
  const { mutate: submitCanvass, isPending: submitting } = useSubmitCanvass(
    params.id,
  );
  const { mutate: generatePos, isPending: generating } =
    useGeneratePurchaseOrders(params.id);

  const items = data?.canvassItems ?? [];
  const suppliers = data?.canvassLines ?? [];
  const decided = data?.status === 'APPROVED';
  const anyAwarded = items.some((i) => i.awardedLineId);
  // Already with an approver: CONFIRM is reached by submitting or by a
  // partial award, and neither wants the submit button offered again.
  const submitted = data?.status === 'CONFIRM';
  const ordersRaised = suppliers.filter((s) => s.generatedPurchaseOrder);
  // An awarded item with no quantity would become an order line for nothing.
  const awardedWithoutQty = items.filter(
    (i) => i.awardedLineId && !i.quantity,
  ).length;

  /** price lookup: supplier id -> item id -> quote */
  const quoteIndex = useMemo(() => {
    const m: Record<string, Record<string, any>> = {};
    suppliers.forEach((s) => {
      m[s.id ?? ''] = {};
      (s.quotes ?? []).forEach((q) => {
        if (q.canvassItemId) m[s.id ?? ''][q.canvassItemId] = q;
      });
    });
    return m;
  }, [suppliers]);

  /** What each supplier would cost for the items they can actually supply. */
  const totals = useMemo(() => {
    const t: Record<string, { value: number; covers: number }> = {};
    suppliers.forEach((s) => {
      let value = 0;
      let covers = 0;
      items.forEach((i) => {
        const q = quoteIndex[s.id ?? '']?.[i.id ?? ''];
        if (q?.available && q.quotedPrice != null) {
          value += (i.quantity ?? 0) * q.quotedPrice;
          covers += 1;
        }
      });
      t[s.id ?? ''] = { value, covers };
    });
    return t;
  }, [suppliers, items, quoteIndex]);

  const saveSupplier = (line: CanvassLineRecord) => {
    if (!data?.id) return;
    const next = line.id
      ? suppliers.map((s) => (s.id === line.id ? line : s))
      : [...suppliers, line];
    update(
      { id: data.id, form: { ...data, canvassLines: next } },
      {
        onSuccess: () => message.success('Supplier saved'),
        onError: () => message.error('Could not save the supplier.'),
      },
    );
    setSupplierDrawer(false);
    setEditingSupplier(null);
  };

  const columns: any[] = [
    {
      title: 'Material',
      dataIndex: 'description',
      fixed: 'left' as const,
      width: 280,
      render: (d: string) => <span className={'text-sm'}>{d}</span>,
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      width: 150,
      render: (_q: number, row: CanvassItemRecord) => (
        <QtyCell
          item={row}
          editable={canEdit && !decided}
          onSave={(quantity) =>
            row.id &&
            saveItem(
              { itemId: row.id, quantity: quantity ?? undefined },
              {
                onError: (e: any) =>
                  message.error(
                    e?.response?.data?.message ??
                      'Could not save the quantity.',
                  ),
              },
            )
          }
        />
      ),
    },
    ...suppliers.map((s) => ({
      title: (
        <div>
          <div className={'font-semibold'}>{s.vendor?.description}</div>
          <div className={'text-xs text-gray-400'}>
            covers {totals[s.id ?? '']?.covers ?? 0}/{items.length} ·{' '}
            {(totals[s.id ?? '']?.value ?? 0).toLocaleString()}
          </div>
        </div>
      ),
      key: s.id,
      width: 150,
      render: (_: unknown, row: CanvassItemRecord) => (
        <QuoteCell
          quote={quoteIndex[s.id ?? '']?.[row.id ?? '']}
          awarded={row.awardedLineId === s.id}
          editable={canEdit && !decided}
          onClick={() => setQuoting({ line: s, item: row })}
        />
      ),
    })),
    {
      title: 'Awarded to',
      key: 'awarded',
      fixed: 'right' as const,
      width: 190,
      render: (_: unknown, row: CanvassItemRecord) => {
        const winner = suppliers.find((s) => s.id === row.awardedLineId);
        if (!winner) {
          return <span className={'text-gray-400'}>—</span>;
        }
        return <Tag color={'success'}>{winner.vendor?.description}</Tag>;
      },
    },
  ];

  return (
    <Spin spinning={isLoading}>
      <div className={'page-wrapper'}>
        <div className={'flex items-center justify-between'}>
          <PageHeaderAction
            moduleName={`Canvass ${data?.code ?? ''}`}
            action={null}
          />
          <div className={'flex gap-2'}>
            {canEdit && !decided && (
              <Button
                onClick={() => {
                  canvassStore.setData(data ?? null);
                  drawerStore.onClose();
                }}
              >
                Edit
              </Button>
            )}
            <Button onClick={() => navigate('/purchase-canvass')}>Back</Button>
          </div>
        </div>

        {ordersRaised.length > 0 && (
          <Alert
            type={'info'}
            showIcon
            className={'mb-4'}
            message={`Order${ordersRaised.length > 1 ? 's' : ''} raised: ${ordersRaised
              .map(
                (s) =>
                  `${s.generatedPurchaseOrder?.code} (${s.vendor?.description})`,
              )
              .join(', ')}`}
          />
        )}

        <Descriptions bordered size={'small'} column={3} className={'mb-4'}>
          <Descriptions.Item label={'Canvass No.'}>
            {data?.code}
          </Descriptions.Item>
          <Descriptions.Item label={'Date'}>
            {data?.canvassDate
              ? convertDate(data.canvassDate, dateFormat)
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={'Status'}>
            <Tag color={statusColour[data?.status ?? 'DRAFT']}>
              {data?.status}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={'For Request'}>
            {data?.purchaseRequest ? (
              <a
                onClick={() =>
                  navigate(`/purchase-request/${data.purchaseRequest?.id}`)
                }
              >
                {data.purchaseRequest.code}
              </a>
            ) : (
              <span className={'text-gray-400'}>not linked to a request</span>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={'Items'}>{items.length}</Descriptions.Item>
          <Descriptions.Item label={'Mainly awarded to'}>
            {data?.selectedVendor?.description ?? (
              <span className={'text-gray-400'}>not yet awarded</span>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={'Notes'} span={3}>
            {data?.notes || '—'}
          </Descriptions.Item>
        </Descriptions>

        <div className={'mb-4'}>
          <TrackingChain api={CANVASS_API} id={params.id} />
        </div>

        <div className={'flex items-center justify-between py-2'}>
          <span className={'text-base font-semibold'}>
            Comparison · {items.length} items × {suppliers.length} suppliers
          </span>
          <div className={'flex gap-2'}>
            {canEdit && !decided && !submitted && (
              <Tooltip title={'Send this comparison for a decision'}>
                <Button
                  type={'primary'}
                  loading={submitting}
                  disabled={suppliers.length === 0}
                  onClick={() =>
                    submitCanvass(
                      { remarks: undefined },
                      {
                        onSuccess: () => message.success('Sent for approval.'),
                        onError: (e: any) =>
                          message.error(
                            e?.response?.data?.message ??
                              'Could not send this for approval.',
                          ),
                      },
                    )
                  }
                >
                  Submit for approval
                </Button>
              </Tooltip>
            )}
            {canEdit && !decided && (
              <Button
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingSupplier(null);
                  setSupplierDrawer(true);
                }}
              >
                Add Supplier
              </Button>
            )}
            {canApprove &&
              !decided &&
              suppliers.map((s) => (
                <Tooltip
                  key={s.id}
                  title={`Award everything ${s.vendor?.description} can supply`}
                >
                  <Button onClick={() => setAwarding(s)}>
                    Award to {s.vendor?.description?.split(' ')[0]}
                  </Button>
                </Tooltip>
              ))}
            {canEdit && anyAwarded && (
              <Tooltip
                title={
                  awardedWithoutQty > 0
                    ? `${awardedWithoutQty} awarded item(s) have no quantity — those lines would be ordered as nothing`
                    : undefined
                }
              >
                <Button
                  type={'primary'}
                  disabled={awardedWithoutQty > 0}
                  loading={generating}
                  onClick={() =>
                    generatePos(undefined, {
                      onSuccess: (pos) =>
                        message.success(
                          `Raised ${pos.length} order${pos.length > 1 ? 's' : ''}: ${pos
                            .map((p) => p.code)
                            .join(', ')}`,
                        ),
                      onError: (e: any) =>
                        message.error(
                          e?.response?.data?.message ??
                            'Could not raise the orders.',
                        ),
                    })
                  }
                >
                  Generate PO
                </Button>
              </Tooltip>
            )}
          </div>
        </div>

        <Table<CanvassItemRecord>
          rowKey={(r) => r.id ?? ''}
          dataSource={items}
          columns={columns}
          size={'small'}
          scroll={{ x: 'max-content' }}
          pagination={items.length > 25 ? { pageSize: 25 } : false}
        />

        <div className={'py-4'}>
          <AttachmentUpload
            label={'Quotations & Supporting Documents'}
            attachments={files}
            api={CANVASS_API}
            recordId={params.id}
            tag={'quotation'}
            canEdit={canEdit}
          />
        </div>

        <CanvassForm api={CANVASS_API} moduleName={'Canvass'} />

        <SupplierDrawer
          open={supplierDrawer}
          line={editingSupplier}
          vendors={vendorData?.data ?? []}
          onClose={() => {
            setSupplierDrawer(false);
            setEditingSupplier(null);
          }}
          onSave={saveSupplier}
        />

        <QuoteDrawer
          open={Boolean(quoting)}
          supplier={quoting?.line.vendor?.description ?? ''}
          item={quoting?.item.description ?? ''}
          quote={
            quoting
              ? quoteIndex[quoting.line.id ?? '']?.[quoting.item.id ?? '']
              : undefined
          }
          onClose={() => setQuoting(null)}
          onSave={(v) => {
            if (!quoting?.line.id || !quoting?.item.id) return;
            saveQuote(
              { lineId: quoting.line.id, itemId: quoting.item.id, ...v },
              {
                onSuccess: () => setQuoting(null),
                onError: () => message.error('Could not save the quote.'),
              },
            );
          }}
        />

        <Modal
          title={`Award to ${awarding?.vendor?.description ?? ''}`}
          open={Boolean(awarding)}
          onCancel={() => setAwarding(null)}
          okText={'Confirm award'}
          onOk={() => {
            if (!awarding?.id) return;
            award(
              { lineId: awarding.id, remarks: awardRemarks },
              {
                onSuccess: () => {
                  message.success('Awarded');
                  setAwarding(null);
                  setAwardRemarks('');
                },
                onError: (e: any) =>
                  message.error(
                    e?.response?.data?.message ?? 'Could not record the award.',
                  ),
              },
            );
          }}
        >
          <p className={'mb-2'}>
            Awards every item this supplier can supply
            {awarding && (
              <>
                {' '}
                — {totals[awarding.id ?? '']?.covers ?? 0} of {items.length}.
                Anything they do not carry stays unawarded for a separate
                decision.
              </>
            )}
          </p>
          <Input.TextArea
            rows={3}
            value={awardRemarks}
            onChange={(e) => setAwardRemarks(e.target.value)}
            placeholder={'Why this supplier?'}
          />
        </Modal>
      </div>
    </Spin>
  );
};

export default CanvassInfoPage;
